import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../api/FastAPI";
import SCHEDULE_LIST_MUTATION from "../../constants/mutationKey";
import {
  getCurrentTimelineQuery,
  invalidateTimelineRelatedQueries,
} from "../../utils/fast";
import useFASTScheduleWebSocket from "../useFASTScheduleWebSocket";
import useTimebounds from "../useTimebounds";

import type { UseMutationResult } from "@tanstack/react-query";

import type { Query } from "../../api/BaseAPI";
import type {
  DeleteScheduleListTimeslotResponse,
  GetScheduleAllScheduleListResponse,
} from "../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../components/routes/FASTChannelRoutes";

interface Params {
  scheduleId: string;
  periodStart: string;
  periodEnd: string;
  itemIdList: Set<string>;
}

interface Context {
  previousScheduleList?: GetScheduleAllScheduleListResponse;
}

function useDeleteScheduleListTimeslotMutation(): UseMutationResult<
  DeleteScheduleListTimeslotResponse,
  unknown,
  Params,
  Context
> {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("editor-fast-channel");
  const queryClient = useQueryClient();

  const { currentDay } = useTimebounds();
  const { confirmUpdatedSchedule } = useFASTScheduleWebSocket();

  const optimisticUpdate = ({
    itemIdList,
    queryKey,
  }: {
    itemIdList: Set<string>;
    queryKey: Query<GetScheduleAllScheduleListResponse>["queryKey"];
  }): {
    previousScheduleList: GetScheduleAllScheduleListResponse | undefined;
  } => {
    const previousScheduleList: GetScheduleAllScheduleListResponse | undefined =
      queryClient.getQueryData(queryKey);

    const newData = previousScheduleList?.data?.filter(
      (item) => !itemIdList.has(item.uuid),
    );

    if (previousScheduleList) {
      queryClient.setQueryData(queryKey, {
        ...previousScheduleList,
        data: newData,
      });
    }

    return { previousScheduleList };
  };

  const deleteScheduleListMediaMutation = useMutation({
    mutationKey: [SCHEDULE_LIST_MUTATION, channelId],
    mutationFn: async ({ periodStart, periodEnd, scheduleId }: Params) =>
      FastAPI.deleteScheduleListTimeslot({
        channelId,
        scheduleId,
        periodStart,
        periodEnd,
      }),
    onMutate: async ({ itemIdList }): Promise<Context> => {
      const { currentQuery } = getCurrentTimelineQuery({
        currentDay,
        channelId,
      });

      await queryClient.cancelQueries(currentQuery);

      const { previousScheduleList } = optimisticUpdate({
        itemIdList,
        queryKey: currentQuery,
      });

      return { previousScheduleList };
    },
    onSuccess: (_, { scheduleId, itemIdList }): void => {
      const { previousQuery, nextQuery } = getCurrentTimelineQuery({
        currentDay,
        channelId,
      });

      optimisticUpdate({
        itemIdList,
        queryKey: nextQuery,
      });

      optimisticUpdate({
        itemIdList,
        queryKey: previousQuery,
      });

      invalidateTimelineRelatedQueries({
        currentDay,
        channelId,
        queryClient,
        scheduleId,
      });

      queryClient.invalidateQueries({
        queryKey: ["getScheduleVariantDetails"],
      });

      confirmUpdatedSchedule(currentDay);

      toast.success(t("programDeleteSuccess"));
    },
    onError: (error, params, context): void => {
      const { currentQuery } = getCurrentTimelineQuery({
        currentDay,
        channelId,
      });

      queryClient.setQueryData(currentQuery, context?.previousScheduleList);
      toast.error(t("programDeleteError"));
      console.error(error, params);
    },
    onSettled: (): void => {
      const { previousQuery, currentQuery, nextQuery } =
        getCurrentTimelineQuery({
          currentDay,
          channelId,
        });
      queryClient.invalidateQueries(previousQuery);
      queryClient.invalidateQueries(currentQuery);
      queryClient.invalidateQueries(nextQuery);
    },
  });

  return deleteScheduleListMediaMutation;
}

export default useDeleteScheduleListTimeslotMutation;
